import React, { FC } from 'react'
import styled from 'styled-components'
import { ModalProps, ModalView } from 'components/organisms/Modal'
import Button from 'components/molecules/Button'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Controller, useForm } from 'react-hook-form'
import InputWithCaptions from 'components/molecules/InputWithCaptions'
import { editFeatureGroupsFn, getFeatureGroupsFn } from 'api'
import { FeatureGroup } from '@types'
import { useParams } from 'react-router-dom'
import { useEffect } from 'react'
import Loader from 'components/molecules/Loader'
import { useTranslation } from 'react-i18next'
import { SwitchField } from '../components/SwitchField'
import { useUserContext } from 'contexts/UserContext'
import { Roles } from 'utils/consts'

// const registrationModeOptions: { label: string; value: number }[] = [
//   { label: 'phone', value: 0 },
//   { label: 'username', value: 1 },
//   { label: 'phone / username', value: 2 },
//   { label: 'phone + precheck', value: 3 },
//   { label: 'username + precheck', value: 4 },
//   { label: 'phone+username+precheck', value: 5 },
// ]

const EditFeatureGroup: FC<ModalProps> = ({ onClose }) => {
  const [t] = useTranslation()
  const params = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const isSuperAdmin = state.admin?.type === Roles.SuperAdmin

  const { data: featureGroups, isLoading } = useQuery(['feature-groups', i_realm], getFeatureGroupsFn)
  const featureGroup = featureGroups?.find(({ i_featuregroup }) => +params.id! === i_featuregroup)

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
  } = useForm<FeatureGroup>()

  const { mutate: editFeatureGroup } = useMutation(
    (data: Partial<FeatureGroup>) => editFeatureGroupsFn({ ...data, i_realm }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('feature-groups')
        onClose()
      },
    }
  )

  const onSubmit = (values: FeatureGroup) => {
    editFeatureGroup(values)
  }

  useEffect(() => {
    if (featureGroup) {
      reset(featureGroup)
    }
  }, [featureGroup, reset])

  if (!featureGroup || isLoading) {
    return <Loader height="30vh" />
  }

  return (
    <ModalView title={t('modal.editFeatureGroup.title')} style={{ maxWidth: '750px' }} onClose={onClose}>
      <Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Field>
            <Controller
              name="name"
              defaultValue=""
              control={control}
              rules={{ required: true }}
              render={({ field: { ref, name, ...props } }) => (
                <InputWithCaptions type="text" caption={t('common.name')} {...props} />
              )}
            />
          </Field>

          <Grid>
            <SwitchField
              name="external_contact_triggers"
              title={t('featureGroup.externalContactTriggers')}
              control={control}
            />
            <SwitchField name="local_contacts_editing" title={t('featureGroup.contactsEditing')} control={control} />
            <SwitchField name="local_contacts_only" title={t('featureGroup.allowLocalContacts')} control={control} />
            <SwitchField
              name="roster_contacts"
              title={t('featureGroup.loadContactsFromServer')}
              control={control}
            />
            <SwitchField name="show_channels" title={t('featureGroup.showChannels')} control={control} />
            <SwitchField name="multiple_devices" title={t('featureGroup.multipleDevices')} control={control} />

            {isSuperAdmin && (
              <>
                <SwitchField name="external_auth" title={t('featureGroup.externalAuth')} control={control} />
                <SwitchField
                  name="auto_message_disappear"
                  title={t('featureGroup.autoMessageDisappear')}
                  control={control}
                />
                <SwitchField name="call_recording" title={t('featureGroup.callRecording')} control={control} />

                <SwitchField name="disable_p2p" title={t('featureGroup.disableP2p')} control={control} />
                <SwitchField name="hide_app" title={t('featureGroup.hideApp')} control={control} />
                <SwitchField
                  name="message_security_options"
                  title={t('featureGroup.messageSecurityOptions')}
                  control={control}
                />
                <SwitchField
                  name="multiple_identities"
                  title={t('featureGroup.multipleIdentities')}
                  control={control}
                />
                <SwitchField
                  name="notarized_receipts"
                  title={t('featureGroup.notarizedReceipts')}
                  control={control}
                />
                <SwitchField name="privacy" title={t('featureGroup.privacy')} control={control} />
                <SwitchField name="promo_credits" title={t('featureGroup.promoCredits')} control={control} />
                <SwitchField name="ptt" title={t('featureGroup.allowPushToTalk')} control={control} />
                <SwitchField
                  name="server_contacts"
                  title={t('featureGroup.allowServerContacts')}
                  control={control}
                />
                <SwitchField
                  name="show_location_during_call"
                  title={t('featureGroup.showLocationDuringCall')}
                  control={control}
                />
                <SwitchField name="unrestricted_voip" title={t('featureGroup.unrestrictedVoip')} control={control} />
                <SwitchField name="video_conf" title={t('featureGroup.videoConference')} control={control} />
                <SwitchField name="wallet" title={t('featureGroup.wallet')} control={control} />

                <SwitchField name="cr_auto_activate" title={t('featureGroup.crAutoActivate')} control={control} />
                <SwitchField name="cr_save_mode" title={t('featureGroup.crSaveMode')} control={control} />
                <SwitchField name="cr_show_controls" title={t('featureGroup.crShowControls')} control={control} />
                <SwitchField
                  name="ptt_default_speaker"
                  title={t('featureGroup.pttDefaultSpeaker')}
                  control={control}
                />
                <SwitchField
                  name="ptt_force_enable_from_contacts"
                  title={t('featureGroup.pttForceEnableFromContacts')}
                  control={control}
                />

                <Controller
                  name="force_jitsi_call_participants_amount"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions
                      type="number"
                      caption={t('featureGroup.forceJitsiCallParticipantsAmount')}
                      {...props}
                    />
                  )}
                />
                <Controller
                  name="contact_joined_notification_mode"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions
                      type="number"
                      caption={t('featureGroup.contactJoinedNotificationMode')}
                      {...props}
                    />
                  )}
                />

                <Controller
                  name="max_daily_transfer"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions type="number" caption={t('featureGroup.maxDailyTransfer')} {...props} />
                  )}
                />
                <Controller
                  name="max_transfer_amount"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions type="number" caption={t('featureGroup.maxTransferAmount')} {...props} />
                  )}
                />
                <Controller
                  name="min_leftover_balance"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions type="number" caption={t('featureGroup.minLeftoverBalance')} {...props} />
                  )}
                />
                {/* <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <GreyParagraph2
                    style={{ marginLeft: '15px', marginBottom: '5px' }}
                    size="11"
                    text="Registration mode"
                  />
                  <Controller
                    name="registration_mode"
                    rules={{ required: true }}
                    defaultValue={registrationModeOptions[0].value}
                    control={control}
                    render={({ field: { ref, onChange, value, ...props } }) => {
                      const val = registrationModeOptions.find(({ value: v }) => v === value)
                      return (
                        <ReactSelect
                          value={val}
                          onChange={({ value }) => {
                            onChange(value)
                          }}
                          options={registrationModeOptions}
                          placeholder="Choose registration mode"
                          {...props}
                        />
                      )
                    }}
                  />
                </div> */}
              </>
            )}
          </Grid>

          <SubmitButton appearance="blue" type="submit" disabled={!isDirty || !isValid}>
            {t('modal.editFeatureGroup.edit')}
          </SubmitButton>
        </Form>
      </Content>
    </ModalView>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Form = styled.form`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
`

const Grid = styled.div`
  display: grid;
  max-height: calc(100vh - 280px);
  grid-template-columns: repeat(auto-fill, 220px);
  grid-gap: 10px;
  width: 100%;
  overflow: auto;
`

const Field = styled.div`
  display: flex;
  margin-bottom: 24px;
  gap: 12px;
  & > * {
    width: 230px;
  }
`

const SubmitButton = styled(Button)`
  margin-top: 32px;
  align-self: flex-end;
`

export default EditFeatureGroup
